import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { eventType } from "@/lib/constants";
import { Film, Clock, MapPin } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import CinemaBrand from "@/components/CinemaBrand";

/**
 * Öffentliches Programm — Zeitungs-Layout für Außen-Display.
 * Keine Interaktion, keine Reservierung. Zeigt die gesamte Wochen-Ansicht
 * mit mehreren Filmen pro Tag im klassischen Kino-/Zeitungs-Stil.
 */
export default function Program() {
  const [rows, setRows] = useState([]);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const load = () => api.get("/public/program").then((r) => setRows(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 90_000);
    const c = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(t); clearInterval(c); };
  }, []);

  const byDate = useMemo(() => rows.reduce((acc, r) => {
    (acc[r.date] = acc[r.date] || []).push(r);
    return acc;
  }, {}), [rows]);
  const sortedDates = useMemo(() => Object.keys(byDate).sort(), [byDate]);

  const kioskUrl = typeof window !== "undefined" ? `${window.location.origin}/kiosk` : "/kiosk";
  const weekLabel = useMemo(() => {
    if (!sortedDates.length) return "";
    const first = new Date(sortedDates[0] + "T00:00:00");
    const last = new Date(sortedDates[sortedDates.length - 1] + "T00:00:00");
    const fmt = (d) => d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
    return sortedDates.length === 1 ? fmt(first) : `${fmt(first)} — ${fmt(last)}.${last.getFullYear()}`;
  }, [sortedDates]);

  return (
    <div className="min-h-screen w-full bg-[#0A0806] text-[#F5F0E6] font-body relative overflow-x-hidden">
      {/* Papier-Textur / Vignette */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-overlay"
        style={{ backgroundImage: "radial-gradient(#D4AF37 1px, transparent 1px)", backgroundSize: "3px 3px" }} />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#3B0C10]/40 via-black/40 to-transparent pointer-events-none" />

      <div className="relative z-10 max-w-[1600px] mx-auto px-8 md:px-14 py-10">
        {/* Masthead — wie Zeitungs-Kopf */}
        <header className="border-y-[3px] border-double border-[#D4AF37]/70 py-6 mb-3">
          <div className="flex items-center justify-between gap-6 flex-wrap">
            <div className="flex items-center gap-6">
              <CinemaBrand size="lg" stacked />
              <div className="hidden md:block h-16 w-px bg-[#D4AF37]/40" />
              <div className="hidden md:block">
                <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">Programm-Zeitung</div>
                <div className="font-heading italic text-3xl text-[#F5F0E6]/95 leading-tight mt-1">Der Spielplan der Woche</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-4xl md:text-5xl tabular-nums text-[#F5F0E6] leading-none">
                {now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-[#A8A398] mt-2">
                {now.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" })}
              </div>
            </div>
          </div>
        </header>

        {/* Unterzeile */}
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/80 border-b border-[#1A1A1A] pb-3 mb-10 font-mono">
          <span>Ausgabe · Nr. {String(now.getWeek?.() || Math.ceil(((now - new Date(now.getFullYear(), 0, 1)) / 86400000 + new Date(now.getFullYear(), 0, 1).getDay() + 1) / 7)).padStart(2, "0")}</span>
          <span className="hidden md:inline">More Than Watching Movies</span>
          <span>{weekLabel || "Diese Woche"}</span>
        </div>

        {/* Tages-Abschnitte im Zeitungs-Look */}
        {sortedDates.length === 0 ? (
          <div className="text-center text-[#A8A398] py-32">
            <Film size={48} className="mx-auto text-[#D4AF37]/60" />
            <div className="mt-6 font-heading text-4xl">Zurzeit keine Vorstellungen</div>
            <div className="text-xs uppercase tracking-[0.4em] mt-3">Wir sehen uns bald wieder.</div>
          </div>
        ) : (
          <div className="space-y-14">
            {sortedDates.map((date, di) => (
              <DaySection key={date} iso={date} shows={byDate[date]} index={di} />
            ))}
          </div>
        )}

        {/* Zeitungs-Footer mit QR */}
        <footer className="mt-16 pt-10 border-t-[3px] border-double border-[#D4AF37]/70 grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">Impressum</div>
            <div className="font-heading text-2xl mt-1">THE MOVIE HOUSE — More Than Watching Movies</div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#575148] mt-3">
              Alle Angaben ohne Gewähr · Kurzfristige Programmänderungen vorbehalten · Filmdaten © themoviedb.org (nicht offiziell)
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0 border-l border-[#1A1A1A] md:pl-8">
            <div className="bg-[#F5F0E6] p-2 rounded">
              <QRCodeCanvas value={kioskUrl} size={98} bgColor="#F5F0E6" fgColor="#0A0A0A" />
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Ticket kaufen</div>
              <div className="font-heading text-3xl mt-1 leading-none">Jetzt scannen</div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-2">Direkt zum Ticketautomat</div>
            </div>
          </div>
        </footer>
      </div>

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: none } }
        .animate-fade-up { animation: fadeUp 700ms cubic-bezier(.2,.7,.2,1) both; }
      `}</style>
    </div>
  );
}

/* -------- Tages-Abschnitt: Zeitungs-Header + Multi-Column Filme -------- */
function DaySection({ iso, shows, index }) {
  const d = new Date(iso + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  const label = diff === 0 ? "Heute" : diff === 1 ? "Morgen" : diff === 2 ? "Übermorgen" : null;
  const weekday = d.toLocaleDateString("de-DE", { weekday: "long" });
  const day = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

  // Filme nach TMDB-ID gruppieren, damit ein Film mit mehreren Vorstellungen auch als ein Block auftaucht
  const groups = useMemo(() => {
    const map = new Map();
    for (const s of shows) {
      const key = s.movie?.tmdb_id || s.movie?.id || s.id;
      const g = map.get(key) || { movie: s.movie, event_type: s.event_type, showings: [] };
      g.showings.push(s);
      map.set(key, g);
    }
    return Array.from(map.values());
  }, [shows]);

  return (
    <section className="animate-fade-up" style={{ animationDelay: `${index * 60}ms` }}>
      {/* Datums-Kopf */}
      <div className="flex items-end gap-6 border-b-2 border-[#D4AF37]/60 pb-4 mb-8">
        <div className="shrink-0 text-center w-24">
          <div className="font-heading text-6xl md:text-7xl text-[#D4AF37] leading-none">{d.getDate().toString().padStart(2, "0")}</div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-1">
            {d.toLocaleDateString("de-DE", { month: "short" }).replace(".", "")}
          </div>
        </div>
        <div className="flex-1 min-w-0 border-l-2 border-[#D4AF37]/40 pl-6">
          <div className="flex items-center gap-3 flex-wrap">
            {label && (
              <span className="text-[10px] uppercase tracking-[0.4em] px-3 py-1 rounded-full border border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5">
                {label}
              </span>
            )}
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398]">{weekday}</span>
          </div>
          <div className="mt-1 font-heading text-3xl md:text-4xl">{day}</div>
        </div>
        <div className="hidden md:flex flex-col items-end gap-1 text-right">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#575148]">
            {shows.length} {shows.length === 1 ? "Vorstellung" : "Vorstellungen"}
          </div>
          {groups.length > 1 && (
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/80">{groups.length} Filme</div>
          )}
        </div>
      </div>

      {/* Filme in Multi-Column Zeitungs-Layout */}
      <div className={`grid gap-6 md:gap-8 ${groups.length === 1 ? "md:grid-cols-1" : "md:grid-cols-2"} lg:${groups.length >= 3 ? "grid-cols-3" : (groups.length === 2 ? "grid-cols-2" : "grid-cols-1")}`}>
        {groups.map((g, gi) => (
          <MovieColumn key={gi} group={g} />
        ))}
      </div>
    </section>
  );
}

/* -------- Ein Film mit einer oder mehreren Vorstellungen -------- */
function MovieColumn({ group }) {
  const m = group.movie;
  const ev = eventType(group.event_type);
  const isEvent = group.event_type && group.event_type !== "regular";

  return (
    <article className="flex gap-5 border-l-2 border-[#D4AF37]/25 pl-5 md:pl-6">
      {/* Poster links */}
      <div className="w-28 md:w-32 shrink-0">
        <div className="aspect-[2/3] bg-[#111] rounded overflow-hidden shadow-lg shadow-black/40 relative">
          {m?.poster_url ? (
            <img src={m.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
              className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#575148]"><Film size={22} /></div>
          )}
          {m?.age_rating && (
            <div className="absolute bottom-2 left-2 text-[9px] font-mono border border-[#D4AF37]/70 text-[#D4AF37] bg-black/70 backdrop-blur px-1.5 py-0.5 rounded">
              {m.age_rating}
            </div>
          )}
        </div>
      </div>

      {/* Titel + Meta + Vorstellungen */}
      <div className="min-w-0 flex-1">
        {isEvent && (
          <div className="inline-block mb-2 text-[9px] uppercase tracking-[0.32em] px-2 py-0.5 rounded-full border"
            style={{ color: ev.color, borderColor: ev.color + "80" }}>
            {ev.icon} {ev.l}
          </div>
        )}
        <h3 className="font-heading text-2xl md:text-3xl text-[#F5F0E6] leading-tight">
          {m?.title_de || m?.title_original || "—"}
        </h3>
        {m?.title_original && m?.title_de && m.title_original !== m.title_de && (
          <div className="italic text-[10px] md:text-xs text-[#575148] mt-0.5 truncate">{m.title_original}</div>
        )}
        <div className="mt-1 text-[10px] md:text-xs uppercase tracking-[0.28em] text-[#A8A398] flex flex-wrap gap-x-3 gap-y-0.5">
          {m?.genre && <span>{m.genre}</span>}
          {m?.duration_min ? (
            <span className="flex items-center gap-1"><Clock size={11} className="text-[#D4AF37]" />{m.duration_min} Min</span>
          ) : null}
        </div>

        {/* Vorstellungen-Chips */}
        <div className="mt-4 flex flex-wrap gap-2">
          {group.showings.map((s) => (
            <div key={s.id} className="inline-flex items-center gap-2 px-2.5 py-1.5 border border-[#D4AF37]/40 rounded bg-black/40">
              <span className="font-mono text-lg md:text-xl tabular-nums text-[#D4AF37] leading-none">{s.start_time}</span>
              <span className="text-[9px] uppercase tracking-[0.32em] text-[#A8A398] flex items-center gap-1">
                <MapPin size={9} className="text-[#D4AF37]" />{s.hall || "Saal"}
              </span>
              {s.format && (
                <span className="text-[9px] uppercase tracking-[0.32em] text-[#575148]">{s.format}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}
